import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { SharedFileRequestContextType } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS } from "../../../Requirements/modules/IdentityVerification/instructions";
import { CreateRequirementDefinitionSharedFileMimeType } from "../../../Requirements/modules/SharedFile/types";
import businessCardImg from "./assets/business-card.svg";
import driversLicenseImg from "./assets/drivers-license.svg";
import headshotImg from "./assets/headshot.svg";
import idVerificationImg from "./assets/id-verification.svg";
import generalLiabilityImg from "./assets/insurance-verification.svg";
import resumeImg from "./assets/resume.svg";
import sharedFileImg from "./assets/shared-file.svg";
import signatureImg from "./assets/signature.svg";
import { GENERAL_LIABILITY_FORM } from "./components/forms/GeneralLiability";
import { PROFESSIONAL_LIABILITY_FORM } from "./components/forms/ProfessionalLiability";
import { STANDARD_BACKGROUND_CHECK_FORM } from "./components/forms/StandardBackgroundCheck";
import { STANDARD_ESIGNATURE_FORM } from "./components/forms/StandardESignature";
import { STANDARD_IDENTITY_VERIFICATION_FORM } from "./components/forms/StandardIdentityVerification";
import { STANDARD_SHARED_FILE_FORM } from "./components/forms/StandardSharedFile";
import { WORK_INJURY_FORM } from "./components/forms/WorkInjury";
import { getSmartSharedFileForm } from "./components/forms/getSmartSharedFileForm";
import {
  BACKGROUND_CHECK,
  BUSINESS_CARD,
  DRIVERS_LICENSE,
  ESignature,
  FILE_UPLOAD,
  GENERAL_LIABILITY,
  HEADSHOT,
  ID_VERIFICATION,
  PROFESSIONAL_LIABILITY,
  RESUME,
  WORK_INJURY
} from "./constants";
import { RequirementDefinitionTemplate } from "./types";

export const TEMPLATES: RequirementDefinitionTemplate[] = [
  // Standard templates
  {
    key: "signature",
    name: "E-signature",
    isCustomName: true,
    icon: "edit-line",
    image: signatureImg,
    requirementType: RequirementType.Signature,
    tier: "Standard",
    modal: {
      description: ESignature.description,
      exampleUseCases: ESignature.exampleUseCases,
      cost: ESignature.cost
    },
    isRequirementWithErrorStates: false,
    form: STANDARD_ESIGNATURE_FORM
  },
  {
    key: "shared-file",
    name: "File upload",
    isCustomName: true,
    icon: "upload",
    image: sharedFileImg,
    requirementType: RequirementType.SharedFile,
    contextType: SharedFileRequestContextType.None,
    tier: "Standard",
    modal: {
      description: FILE_UPLOAD.description,
      exampleUseCases: FILE_UPLOAD.exampleUseCases,
      cost: FILE_UPLOAD.cost
    },
    isRequirementWithErrorStates: false,
    form: STANDARD_SHARED_FILE_FORM
  },

  // Smart (Billable) document verification templates (managed off-platform)
  {
    key: "drivers-license",
    name: "Driver’s license",
    helperText: "Document verification",
    icon: "id",
    image: driversLicenseImg,
    requirementType: RequirementType.SharedFile,
    contextType: SharedFileRequestContextType.DriversLicense,
    tier: "Smart",
    isBillable: true,
    payerInfo: {
      description: DRIVERS_LICENSE.description,
      disclaimer: DRIVERS_LICENSE.disclaimer,
      cost: DRIVERS_LICENSE.cost
    },
    modal: {
      description: DRIVERS_LICENSE.description,
      disclaimer: DRIVERS_LICENSE.disclaimer,
      cost: DRIVERS_LICENSE.cost
    },

    instructions: DRIVERS_LICENSE.instructions,
    isRequirementWithErrorStates: true,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Document]: true,
      [CreateRequirementDefinitionSharedFileMimeType.Image]: true
    }),
    costPerSubmission: 1.99,
    singleInstance: true
  },
  {
    key: "headshot",
    name: "Headshot (photo)",
    icon: "user-fill",
    helperText: "Document verification",
    image: headshotImg,
    requirementType: RequirementType.SharedFile,
    contextType: SharedFileRequestContextType.Headshot,
    tier: "Smart",
    isBillable: true,
    payerInfo: {
      description: HEADSHOT.description,
      exampleUseCases: HEADSHOT.exampleUseCases,
      cost: HEADSHOT.cost
    },
    instructions: HEADSHOT.instructions,
    modal: {
      description: HEADSHOT.description,
      exampleUseCases: HEADSHOT.exampleUseCases,
      cost: HEADSHOT.cost
    },
    isRequirementWithErrorStates: true,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Image]: true
    }),
    costPerSubmission: 1.99,
    singleInstance: true
  },
  {
    key: "resume",
    name: "Resume",
    icon: "file",
    helperText: "Document verification",
    image: resumeImg,
    requirementType: RequirementType.SharedFile,
    contextType: SharedFileRequestContextType.Resume,
    tier: "Smart",
    isBillable: true,
    payerInfo: {
      description: RESUME.description,
      exampleUseCases: RESUME.exampleUseCases,
      cost: RESUME.cost
    },
    modal: {
      description: RESUME.description,
      exampleUseCases: RESUME.exampleUseCases,
      cost: RESUME.cost
    },
    isRequirementWithErrorStates: true,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Document]: true,
      [CreateRequirementDefinitionSharedFileMimeType.Other]: true
    }),
    costPerSubmission: 1.99,
    singleInstance: true,
    instructions: RESUME.instructions
  },
  {
    key: "business-card",
    name: "Business card",
    icon: "details",
    helperText: "Document verification",
    image: businessCardImg,
    requirementType: RequirementType.SharedFile,
    contextType: SharedFileRequestContextType.BusinessCard,
    tier: "Smart",
    isBillable: true,
    payerInfo: {
      description: BUSINESS_CARD.description,
      exampleUseCases: BUSINESS_CARD.exampleUseCases,
      cost: BUSINESS_CARD.cost
    },
    modal: {
      description: BUSINESS_CARD.description,
      exampleUseCases: BUSINESS_CARD.exampleUseCases,
      cost: BUSINESS_CARD.cost
    },
    isRequirementWithErrorStates: true,
    form: getSmartSharedFileForm({
      [CreateRequirementDefinitionSharedFileMimeType.Image]: true,
      [CreateRequirementDefinitionSharedFileMimeType.Document]: true
    }),
    costPerSubmission: 1.99,
    singleInstance: true,
    instructions: BUSINESS_CARD.instructions
  },

  // SmartAdvanced templates
  {
    key: "id-verification",
    singleInstance: true,
    name: "ID verification",
    icon: "id",
    helperText: "Biometrics matching",
    image: idVerificationImg,
    requirementType: RequirementType.IdentityVerification,
    tier: "SmartAdvanced",
    isBillable: true,
    payerInfo: {
      description: ID_VERIFICATION.description,
      exampleUseCases: ID_VERIFICATION.exampleUseCases,
      cost: ID_VERIFICATION.cost
    },
    modal: {
      description: ID_VERIFICATION.description,
      exampleUseCases: ID_VERIFICATION.exampleUseCases,
      cost: ID_VERIFICATION.cost
    },
    isRequirementWithErrorStates: true,
    form: STANDARD_IDENTITY_VERIFICATION_FORM,
    costPerSubmission: 5,
    instructions: IDENTITY_VERIFICATION_REQUIREMENT_INSTRUCTIONS
  },
  {
    key: "general-liability",
    name: "General liability",
    icon: "shield-check-fill",
    helperText: "Insurance verification",
    image: generalLiabilityImg,
    requirementType: RequirementType.InsuranceCoverage,
    policyType: ["GeneralLiability"],
    tier: "SmartAdvanced",
    isBillable: true,
    modal: {
      description: GENERAL_LIABILITY.description,
      exampleUseCases: GENERAL_LIABILITY.exampleUseCases,
      cost: GENERAL_LIABILITY.cost
    },
    form: GENERAL_LIABILITY_FORM,
    costPerSubmission: 15,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true,
    isPolicy: true,
    singleInstance: true
  },
  {
    key: "professional-liability",
    name: "Professional liability",
    icon: "shield-check-fill",
    helperText: "Insurance verification",
    image: generalLiabilityImg,
    requirementType: RequirementType.InsuranceCoverage,
    policyType: ["ProfessionalLiability"],
    tier: "SmartAdvanced",
    isBillable: true,
    modal: {
      description: PROFESSIONAL_LIABILITY.description,
      exampleUseCases: PROFESSIONAL_LIABILITY.exampleUseCases,
      cost: PROFESSIONAL_LIABILITY.cost
    },
    form: PROFESSIONAL_LIABILITY_FORM,
    costPerSubmission: 15,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true,
    isPolicy: true,
    singleInstance: true
  },
  {
    key: "work-injury-coverage",
    name: "Work injury coverage",
    icon: "shield-check-fill",
    helperText: "Insurance verification",
    image: generalLiabilityImg,
    requirementType: RequirementType.InsuranceCoverage,
    policyType: ["OccupationalAccident"],
    tier: "SmartAdvanced",
    isBillable: true,
    modal: {
      description: WORK_INJURY.description,
      exampleUseCases: WORK_INJURY.exampleUseCases,
      cost: WORK_INJURY.cost
    },

    form: WORK_INJURY_FORM,
    costPerSubmission: 15,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true,
    isPolicy: true,
    singleInstance: true
  },
  /* {
    key: "auto-liability",
    name: "Auto liability",
    icon: "car",
    helperText: "Insurance verification",
    image: autoInsuranceVerificationImg,
    requirementType: RequirementType.InsuranceCoverage,
    policyType: "AutomobileLiability",
    tier: "SmartAdvanced",
    isBillable: true,
    modal: {
      description: AUTO_LIABILITY.description,
      exampleUseCases: AUTO_LIABILITY.exampleUseCases,
      cost: AUTO_LIABILITY.cost
    },
    form: AUTO_LIABILITY_FORM,
    instructions: getAutoInsuranceCardInstructions(),
    validations: Object.values(AUTO_INSURANCE_CARD_JOBS).map(job => ({
      title: job.title,
      description: job.description
    })),
    costPerSubmission: 1.99,
    isRequirementWithErrorStates: true,
    isSmartAdvanced: true
  }, */
  {
    key: "background-check",
    name: "Background check",
    icon: "shield-check-fill",
    image: idVerificationImg,
    requirementType: RequirementType.BackgroundCheck,
    tier: "SmartAdvanced",
    isBillable: true,
    isCustomName: true,
    modal: {
      description: BACKGROUND_CHECK.description,
      exampleUseCases: BACKGROUND_CHECK.exampleUseCases,
      cost: BACKGROUND_CHECK.cost
    },
    form: STANDARD_BACKGROUND_CHECK_FORM,
    isRequirementWithErrorStates: true
  },
  {
    hidden: true,
    key: "shared-file-coi-gwi",
    name: "File upload",
    icon: "upload",
    requirementType: RequirementType.SharedFile,
    helperText: "COI validation",
    contextType: [
      SharedFileRequestContextType.GeneralLiabilityCertificateOfInsurance,
      SharedFileRequestContextType.WorkersCompCertificateOfInsurance
    ],
    tier: "Smart",
    isBillable: true,
    isCustomName: true,
    modal: {
      description: "",
      exampleUseCases: "",
      cost: ""
    },
    form: null,
    isRequirementWithErrorStates: true
  },
  {
    hidden: true,
    key: "shared-file-coi-ai",
    name: "File upload",
    icon: "upload",
    requirementType: RequirementType.SharedFile,
    helperText: "Auto insurance verification",
    contextType: SharedFileRequestContextType.AutoInsuranceCard,
    tier: "Smart",
    isBillable: true,
    isCustomName: true,
    modal: {
      description: "",
      exampleUseCases: "",
      cost: ""
    },
    form: null,
    isRequirementWithErrorStates: true
  }
];
